import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Copy,
  CheckCircle,
  ClipboardText,
  FileHtml,
  FloppyDisk,
  Trash,
  UserPlus,
  Users,
  UploadSimple,
  Spinner,
} from "@phosphor-icons/react";
import api from "../api";

/**
 * ZMEX Email Signature installer + editor.
 *
 * Every recipient email client (Gmail, Apple Mail, Outlook, Yahoo) fetches
 * the animated + static assets directly from this Hub's /email-assets/
 * folder, so the finished signature has zero dependency on any third-party
 * signature host.
 *
 * The editor lets an admin build signatures for any employee. Presets
 * are persisted in localStorage under `zmex.signature.presets`.
 */

const STORAGE_KEY = "zmex.signature.presets";
const ACTIVE_KEY = "zmex.signature.active";

const KEVIN_PRESET = {
  id: "kevin",
  label: "Kevin Zurita",
  name: "Kevin Zurita",
  jobTitle: "Owner / Founder / CEO",
  company: "ZMEX DISPOSAL",
  direct: "(773)642-1179",
  office: "(407)305-2756",
  email: "zkevin@zmexdisposal.com",
  website: "https://zmexdisposal.com/",
  instagram: "https://www.instagram.com/zmexdisposal/",
  linkedin: "https://www.linkedin.com/in/zuritakevin",
  facebook: "https://www.facebook.com/profile.php?id=61583165666063#",
  google: "https://share.google/F5ZetZd6FAnpgd8tN",
  quote: "https://zmexdisposal.com/contact",
  profileAsset: "profile.gif",
  hidden: {},
};

const BLANK_PRESET = {
  id: "",
  label: "New Employee",
  name: "First Last",
  jobTitle: "Job Title",
  company: "ZMEX DISPOSAL",
  direct: "",
  office: "(407)305-2756",
  email: "employee@zmexdisposal.com",
  website: "https://zmexdisposal.com/",
  instagram: "https://www.instagram.com/zmexdisposal/",
  linkedin: "",
  facebook: "https://www.facebook.com/profile.php?id=61583165666063#",
  google: "https://share.google/F5ZetZd6FAnpgd8tN",
  quote: "https://zmexdisposal.com/contact",
  profileAsset: "default-profile.gif",
  // New employees start with the fields the user usually skips already
  // hidden — they can flip them back on individually if needed.
  hidden: { direct: true, linkedin: true },
};

function assetOrigin() {
  if (typeof window === "undefined") return "https://hub.zmexdisposal.com";
  return window.location.origin;
}

function buildSignatureHTML(origin, d) {
  const A = (name) => `${origin}/email-assets/${name}`;
  const telHref = (n) => `tel:${(n || "").replace(/[^+\d]/g, "")}`;
  const hidden = d.hidden || {};
  const show = (k) => !hidden[k] && !!(d[k] ?? "").toString().trim();
  const showFlag = (k) => !hidden[k]; // for non-textual entries (e.g. quote button always has a URL default)
  // profileAsset can be either a bare filename (default assets shipped
  // under /email-assets/) OR an absolute path (uploaded via the admin
  // endpoint, served at /api/uploads/signature_profiles/*).
  const profileRaw = d.profileAsset || "default-profile.gif";
  const profileSrc = /^(https?:)?\//.test(profileRaw)
    ? (profileRaw.startsWith("http") ? profileRaw : `${origin}${profileRaw}`)
    : A(profileRaw);

  // Build the social icon column dynamically so hidden rows disappear
  // entirely (both the icon and its spacing). If every social is hidden
  // we drop the entire bordered column and its 20px gap.
  const socialRow = (key, alt, gif) =>
    show(key)
      ? `                    <tr><td style="padding:5px 0 0 0;"><a href="${d[key]}" target="_blank"><img alt="${alt}" src="${A(gif)}" width="24" style="display:block;border:0;"></a></td></tr>`
      : "";
  const socialRows = [
    socialRow("website", "Website", "web-icon.gif"),
    socialRow("instagram", "Instagram", "insta-icon.gif"),
    socialRow("linkedin", "LinkedIn", "linkedin-icon.gif"),
    socialRow("facebook", "Facebook", "facebook-icon.gif"),
    socialRow("google", "Google", "google-icon.gif"),
  ].filter(Boolean).join("\n");
  const socialColumn = socialRows
    ? `                <td align="left" valign="middle" style="border:1px solid #e2e2e2;border-radius:5px;padding:3px 8px 8px 8px;">
                  <table cellpadding="0" cellspacing="0" border="0">
${socialRows}
                  </table>
                </td>
                <td style="padding-right:20px;"></td>`
    : "";

  const contactRows = [
    show("company")
      ? `                          <tr><td><span style="font-weight:bold;color:#000000;font-size:12px;font-family:Inter,Arial,Helvetica,sans-serif;">${d.company}</span></td></tr>`
      : "",
    show("direct")
      ? `                          <tr><td nowrap="nowrap"><a href="${telHref(d.direct)}" style="text-decoration:none;font-weight:bold;color:#000000;font-size:12px;font-family:Inter,Arial,Helvetica,sans-serif;">Direct:&nbsp;<span style="font-weight:normal;color:#000000;font-size:12px;">${d.direct}</span></a></td></tr>`
      : "",
    show("office")
      ? `                          <tr><td nowrap="nowrap"><a href="${telHref(d.office)}" style="text-decoration:none;font-weight:bold;color:#000000;font-size:12px;font-family:Inter,Arial,Helvetica,sans-serif;">Office:&nbsp;<span style="font-weight:normal;color:#000000;font-size:12px;">${d.office}</span></a></td></tr>`
      : "",
    show("email")
      ? `                          <tr><td nowrap="nowrap"><a href="mailto:${d.email}" style="text-decoration:none;color:#000000;font-size:12px;font-family:Inter,Arial,Helvetica,sans-serif;"><span style="color:#000000;font-size:12px;">${d.email}</span></a></td></tr>`
      : "",
  ].filter(Boolean).join("\n");

  const quoteRow = (showFlag("quote") && (d.quote || "").trim())
    ? `                          <tr>
                            <td style="padding:5px 0 0 0;">
                              <table cellpadding="0" cellspacing="0" border="0">
                                <tr>
                                  <td><a href="${d.quote}" target="_blank"><img alt="Get a Quote" src="${A('getaquote.png')}" width="80" style="display:block;border:0;"></a></td>
                                </tr>
                              </table>
                            </td>
                          </tr>`
    : "";

  const nameCell = show("name")
    ? `<td align="left" valign="middle" nowrap="nowrap"><span style="font-weight:bold;color:#000000;font-size:16px;font-family:Inter,Arial,Helvetica,sans-serif;">${d.name}</span></td>
                                        <td align="left" valign="middle" style="padding-left:5px;"><img alt="Verified" width="15" height="15" src="${A('verified.gif')}" style="display:inline;border:0;"></td>`
    : "";
  const titleRow = show("jobTitle")
    ? `                                <tr>
                                  <td nowrap="nowrap"><span style="color:#000000;font-size:12px;font-family:Inter,Arial,Helvetica,sans-serif;">${d.jobTitle}</span></td>
                                </tr>`
    : "";

  return (
`<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;font-size:10px;font-family:Inter,Arial,Helvetica,sans-serif;">
  <tr>
    <td style="line-height:16px;font-family:Inter,Arial,Helvetica,sans-serif;">
      <table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:separate;">
        <tr>
          <td align="left" valign="top" style="padding:15px;border:1px solid #e2e2e2;border-radius:5px;">
            <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:separate;">
              <tr>
${socialColumn}
                <td align="left" valign="middle">
                  <table width="100%" cellpadding="0" cellspacing="0" border="0">
                    <tr>
                      <td align="left" valign="middle">
                        <table width="100%" cellpadding="0" cellspacing="0" border="0">
                          <tr>
                            <td><a href="${d.website || 'https://zmexdisposal.com'}" target="_blank"><img alt="ZMEX Disposal" src="${A('zmex-logo.png')}" width="135" style="display:block;border:0;"></a></td>
                          </tr>
                          <tr>
                            <td style="padding:0 0 10px 0;">
                              <table cellpadding="0" cellspacing="0" border="0">
                                <tr>
                                  <td style="padding:15px 0 0 0;">
                                    <table cellpadding="0" cellspacing="0" border="0">
                                      <tr>
                                        ${nameCell}
                                      </tr>
                                    </table>
                                  </td>
                                </tr>
${titleRow}
                              </table>
                            </td>
                          </tr>
${contactRows}
${quoteRow}
                        </table>
                      </td>
                      <td align="left" valign="middle" style="padding:0 0 0 15px;"><img alt="${d.name || 'ZMEX Disposal'}" src="${profileSrc}" width="135" style="display:inline;border-radius:0;border:0;"></td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`
  );
}

function loadPresets() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) return parsed;
    }
  } catch {}
  return [KEVIN_PRESET];
}

function savePresets(list) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch {}
}

function newId() {
  return `p_${Math.random().toString(36).slice(2, 9)}`;
}

const FIELDS = [
  { key: "label", label: "Preset Label", placeholder: "e.g. Kevin Zurita", required: true },
  { key: "name", label: "Full Name", required: true },
  { key: "jobTitle", label: "Job Title", canHide: true },
  { key: "company", label: "Company", canHide: true },
  { key: "direct", label: "Direct Phone", canHide: true },
  { key: "office", label: "Office Phone", canHide: true },
  { key: "email", label: "Email", canHide: true },
  { key: "website", label: "Website URL", canHide: true },
  { key: "instagram", label: "Instagram URL", canHide: true },
  { key: "linkedin", label: "LinkedIn URL", canHide: true },
  { key: "facebook", label: "Facebook URL", canHide: true },
  { key: "google", label: "Google Business URL", canHide: true },
  { key: "quote", label: "Get-a-Quote URL", canHide: true },
];

// Diagonal-shine family — 6 variations within Kevin's visual family,
// plus a "static" no-animation option for anyone who wants a plain
// headshot instead of a moving one.
const STYLE_OPTIONS = [
  { id: "shine", label: "Shine", description: "Diagonal light streak, left→right (Kevin's default)" },
  { id: "shine-reverse", label: "Reverse", description: "Diagonal light streak, right→left" },
  { id: "slice-down", label: "Slice Down", description: "Steep diagonal band, top→bottom" },
  { id: "slice-up", label: "Slice Up", description: "Steep diagonal band, bottom→top" },
  { id: "slice-split", label: "Split", description: "Two light bands meet, then part" },
  { id: "double-shine", label: "Double", description: "Two parallel diagonal streaks" },
  { id: "static", label: "No Animation", description: "Plain headshot, no motion" },
];

export default function EmailSignature() {
  const origin = assetOrigin();
  const [presets, setPresets] = useState(loadPresets);
  const [activeId, setActiveId] = useState(() => {
    try { return localStorage.getItem(ACTIVE_KEY) || presets[0]?.id || "kevin"; } catch { return "kevin"; }
  });
  const [draft, setDraft] = useState(() => {
    const p = presets.find((x) => x.id === activeId) || presets[0] || KEVIN_PRESET;
    return { ...p };
  });
  const [copied, setCopied] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [uploading, setUploading] = useState(false);
  const previewRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => { savePresets(presets); }, [presets]);
  useEffect(() => { try { localStorage.setItem(ACTIVE_KEY, activeId); } catch {} }, [activeId]);

  const html = useMemo(() => buildSignatureHTML(origin, draft), [origin, draft]);

  function selectPreset(id) {
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    setActiveId(id);
    setDraft({ ...p });
    setDirty(false);
  }

  function updateField(key, value) {
    setDraft((d) => ({ ...d, [key]: value }));
    setDirty(true);
  }

  function toggleHidden(key) {
    setDraft((d) => {
      const next = { ...(d.hidden || {}) };
      if (next[key]) delete next[key]; else next[key] = true;
      return { ...d, hidden: next };
    });
    setDirty(true);
  }

  function saveActive() {
    const idx = presets.findIndex((p) => p.id === activeId);
    let id = draft.id || activeId || newId();
    const updated = { ...draft, id };
    let next;
    if (idx >= 0) {
      next = [...presets];
      next[idx] = updated;
    } else {
      next = [...presets, updated];
    }
    setPresets(next);
    setActiveId(id);
    setDraft(updated);
    setDirty(false);
  }

  function addNew() {
    const id = newId();
    const fresh = { ...BLANK_PRESET, id, label: "New Employee" };
    const next = [...presets, fresh];
    setPresets(next);
    setActiveId(id);
    setDraft(fresh);
    setDirty(false);
  }

  function deleteActive() {
    if (presets.length <= 1) {
      alert("Keep at least one preset.");
      return;
    }
    if (!confirm(`Delete preset "${draft.label}"?`)) return;
    const next = presets.filter((p) => p.id !== activeId);
    setPresets(next);
    const first = next[0];
    setActiveId(first.id);
    setDraft({ ...first });
    setDirty(false);
  }

  async function onPickPhoto(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      alert("Please pick an image file (JPG, PNG, or WebP).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/email-signature/profile", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // The backend renders every animation style at once and returns the
      // full URL map so switching styles doesn't require another upload.
      setDraft((d) => ({
        ...d,
        profileAsset: data.variants?.[data.default] || data.url,
        profileVariants: data.variants || null,
        profileStyle: data.default,
      }));
      setDirty(true);
    } catch (err) {
      alert(`Upload failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setUploading(false);
    }
  }

  function resetPhoto() {
    setDraft((d) => ({ ...d, profileAsset: "default-profile.gif", profileVariants: null, profileStyle: null }));
    setDirty(true);
  }

  function pickStyle(styleId) {
    setDraft((d) => {
      const url = d.profileVariants?.[styleId];
      if (!url) return d;
      return { ...d, profileAsset: url, profileStyle: styleId };
    });
    setDirty(true);
  }

  async function copyRich() {
    try {
      if (window.ClipboardItem && navigator.clipboard?.write) {
        const item = new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob(
            [`${draft.name} — ${draft.jobTitle} — ${draft.company}\nDirect: ${draft.direct}  Office: ${draft.office}\n${draft.email}  ${draft.website}`],
            { type: "text/plain" },
          ),
        });
        await navigator.clipboard.write([item]);
      } else {
        selectSignature();
        document.execCommand("copy");
        window.getSelection?.().removeAllRanges();
      }
      setCopied("rich");
      setTimeout(() => setCopied(null), 2500);
    } catch {
      alert("Copy failed. Use 'Select Signature' and press Cmd/Ctrl+C.");
    }
  }

  function selectSignature() {
    const el = previewRef.current;
    if (!el) return;
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  }

  async function copyHTMLSource() {
    try {
      await navigator.clipboard.writeText(html);
      setCopied("html");
      setTimeout(() => setCopied(null), 2500);
    } catch {
      alert("Copy failed.");
    }
  }

  const bytes = new Blob([html]).size;
  const domains = Array.from(new Set([...html.matchAll(/https?:\/\/([^/"'\s]+)/g)].map((m) => m[1]))).sort();

  const btnBase = { border: "none", padding: "10px 14px", borderRadius: 4, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 13 };

  return (
    <div style={{ minHeight: "100vh", background: "#f4f5f7", padding: 32, fontFamily: "Inter, sans-serif", color: "#0f1622" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.3em", fontWeight: 700, color: "#CFAB7A", textTransform: "uppercase" }}>ZMEX Hub</div>
          <h1 style={{ fontSize: 32, fontWeight: 900, margin: "4px 0 6px" }}>Email Signature Studio</h1>
          <p style={{ color: "#5C738A", fontSize: 14, margin: 0, maxWidth: 720 }}>
            Edit any field, save presets per employee, and copy the ready-to-paste signature. Assets served from <code style={{ background: "#e9eef3", padding: "2px 6px", borderRadius: 4 }}>{origin}/email-assets/</code>. New employees inherit the default ZMEX-branded profile animation until a custom one is uploaded.
          </p>
        </div>

        {/* Presets bar */}
        <section style={{ background: "#fff", border: "1px solid #e2e2e2", borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#5C738A", fontSize: 12, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase" }}>
              <Users size={16} weight="bold" /> Presets
            </div>
            <select
              data-testid="preset-select"
              value={activeId}
              onChange={(e) => selectPreset(e.target.value)}
              style={{ padding: "8px 12px", border: "1px solid #cfd6dd", borderRadius: 4, fontSize: 14, minWidth: 220 }}
            >
              {presets.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
            <button onClick={addNew} data-testid="add-preset" style={{ ...btnBase, background: "#CFAB7A", color: "#0f1622" }}>
              <UserPlus size={16} weight="bold" /> New Employee
            </button>
            <button onClick={saveActive} data-testid="save-preset" style={{ ...btnBase, background: dirty ? "#012340" : "#e9eef3", color: dirty ? "#fff" : "#5C738A" }}>
              <FloppyDisk size={16} weight="bold" /> {dirty ? "Save Changes" : "Saved"}
            </button>
            <button onClick={deleteActive} data-testid="delete-preset" style={{ ...btnBase, background: "#fff", color: "#b23", border: "1px solid #b23" }}>
              <Trash size={16} weight="bold" /> Delete
            </button>
          </div>
        </section>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 380px) 1fr", gap: 20, alignItems: "start" }}>
          {/* Editor */}
          <section style={{ background: "#fff", border: "1px solid #e2e2e2", borderRadius: 8, padding: 20 }}>
            <div style={{ fontSize: 10, letterSpacing: "0.3em", fontWeight: 700, color: "#5C738A", textTransform: "uppercase", marginBottom: 12 }}>Editor</div>

            {/* Profile photo uploader */}
            <div style={{ marginBottom: 14, padding: 12, background: "#f7f8fa", border: "1px dashed #cfd6dd", borderRadius: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#5C738A", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Profile Photo</div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <img
                  data-testid="profile-thumb"
                  src={/^(https?:)?\//.test(draft.profileAsset || "") ? (draft.profileAsset.startsWith("http") ? draft.profileAsset : `${origin}${draft.profileAsset}`) : `${origin}/email-assets/${draft.profileAsset || "default-profile.gif"}`}
                  alt="Profile"
                  width="72"
                  height="72"
                  style={{ borderRadius: 4, border: "1px solid #e2e2e2", display: "block" }}
                />
                <div style={{ flex: 1 }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={onPickPhoto}
                    style={{ display: "none" }}
                    data-testid="profile-file-input"
                  />
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="upload-photo-btn"
                      style={{ ...btnBase, background: "#012340", color: "#fff", padding: "8px 12px", fontSize: 12 }}
                    >
                      {uploading ? <><Spinner size={14} weight="bold" className="animate-spin" /> Rendering…</> : <><UploadSimple size={14} weight="bold" /> Upload Photo</>}
                    </button>
                    <button
                      onClick={resetPhoto}
                      disabled={uploading}
                      data-testid="reset-photo-btn"
                      style={{ ...btnBase, background: "#fff", color: "#5C738A", border: "1px solid #cfd6dd", padding: "8px 12px", fontSize: 12 }}
                    >
                      Use default
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: "#5C738A", marginTop: 6 }}>
                    Square photo works best. Upload once — we render every animation style at 135×135 so you can switch instantly.
                  </div>
                </div>
              </div>

              {(() => {
                const hasVariants = !!draft.profileVariants;
                const isUploaded = /\/api\/uploads\/signature_profiles\//.test(draft.profileAsset || "");
                // If we already have variants → normal picker.
                // If the preset points at an uploaded photo but variants are
                // missing (older save format, or the uploads folder was
                // reset after a deploy), tell the admin to re-upload so all
                // 6 styles regenerate.
                if (hasVariants) {
                  return (
                    <div style={{ marginTop: 12 }} data-testid="style-picker">
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#5C738A", textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 6 }}>Animation Style</div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(84px, 1fr))", gap: 8 }}>
                        {STYLE_OPTIONS.map((s) => {
                          const url = draft.profileVariants[s.id];
                          if (!url) return null;
                          const active = draft.profileStyle === s.id;
                          const src = url.startsWith("http") ? url : `${origin}${url}`;
                          return (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => pickStyle(s.id)}
                              data-testid={`style-${s.id}`}
                              title={s.description}
                              style={{
                                padding: 4,
                                border: `2px solid ${active ? "#CFAB7A" : "#e2e2e2"}`,
                                background: active ? "#fff7ea" : "#fff",
                                borderRadius: 6,
                                cursor: "pointer",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <img src={src} alt={s.label} width="72" height="72" style={{ borderRadius: 3, display: "block" }} />
                              <span style={{ fontSize: 10, fontWeight: 800, color: active ? "#012340" : "#5C738A", letterSpacing: "0.1em", textTransform: "uppercase" }}>{s.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                if (isUploaded) {
                  return (
                    <div style={{ marginTop: 12, padding: 12, background: "#fff7ea", border: "1px solid #CFAB7A", borderRadius: 6 }} data-testid="reupload-hint">
                      <div style={{ fontSize: 12, color: "#012340", fontWeight: 700, marginBottom: 6 }}>
                        Re-upload to unlock the 6 animation styles
                      </div>
                      <div style={{ fontSize: 12, color: "#5C738A", marginBottom: 8 }}>
                        This preset was created before we added the new animation library, or its files were cleared during a recent deploy. Click <b>Upload Photo</b> again to regenerate all styles.
                      </div>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        style={{ ...btnBase, background: "#012340", color: "#fff", padding: "8px 14px", fontSize: 12 }}
                      >
                        <UploadSimple size={14} weight="bold" /> Re-upload to regenerate
                      </button>
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            {FIELDS.map((f) => {
              const isHidden = !!draft.hidden?.[f.key];
              return (
                <label key={f.key} style={{ display: "block", marginBottom: 10 }}>
                  <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11, fontWeight: 700, color: isHidden ? "#a0aab5" : "#5C738A", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>
                    <span>{f.label}{isHidden ? " (hidden)" : ""}</span>
                    {f.canHide && (
                      <button
                        type="button"
                        onClick={() => toggleHidden(f.key)}
                        data-testid={`toggle-${f.key}`}
                        style={{ border: "none", background: isHidden ? "#e9eef3" : "#CFAB7A", color: isHidden ? "#5C738A" : "#0f1622", padding: "3px 8px", borderRadius: 3, fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", cursor: "pointer", textTransform: "uppercase" }}
                      >
                        {isHidden ? "Show" : "Hide"}
                      </button>
                    )}
                  </span>
                  <input
                    data-testid={`field-${f.key}`}
                    value={draft[f.key] || ""}
                    onChange={(e) => updateField(f.key, e.target.value)}
                    placeholder={f.placeholder || ""}
                    disabled={isHidden}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #cfd6dd", borderRadius: 4, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", background: isHidden ? "#f4f5f7" : "#fff", color: isHidden ? "#a0aab5" : "#0f1622" }}
                  />
                </label>
              );
            })}
          </section>

          {/* Preview + actions */}
          <div>
            <section style={{ background: "#fff", border: "1px solid #e2e2e2", borderRadius: 8, padding: 24, marginBottom: 16 }}>
              <div style={{ fontSize: 10, letterSpacing: "0.3em", fontWeight: 700, color: "#5C738A", textTransform: "uppercase", marginBottom: 12 }}>Live Preview</div>
              <div ref={previewRef} data-testid="signature-preview" dangerouslySetInnerHTML={{ __html: html }} />
            </section>

            <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
              <button onClick={copyRich} data-testid="copy-rich" style={{ ...btnBase, background: "#012340", color: "#fff", padding: "14px 18px" }}>
                {copied === "rich" ? <><CheckCircle size={18} weight="bold" /> Copied!</> : <><Copy size={18} weight="bold" /> Copy Signature</>}
              </button>
              <button onClick={selectSignature} data-testid="select-signature" style={{ ...btnBase, background: "#fff", color: "#012340", border: "1px solid #012340", padding: "14px 18px" }}>
                <ClipboardText size={18} weight="bold" /> Select Signature
              </button>
              <button onClick={copyHTMLSource} data-testid="copy-html" style={{ ...btnBase, background: "#fff", color: "#5C738A", border: "1px solid #cfd6dd", padding: "14px 18px" }}>
                {copied === "html" ? <><CheckCircle size={18} weight="bold" /> Copied!</> : <><FileHtml size={18} weight="bold" /> Copy HTML Source</>}
              </button>
            </section>

            <section style={{ background: "#fff", border: "1px solid #e2e2e2", borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 10, letterSpacing: "0.3em", fontWeight: 700, color: "#5C738A", textTransform: "uppercase", marginBottom: 8 }}>Diagnostics</div>
              <div style={{ fontSize: 13, marginBottom: 6 }}>
                <b>HTML size:</b> {bytes.toLocaleString()} bytes ({(bytes/1024).toFixed(1)} KB) &middot; <b>External domains:</b> {domains.length}
              </div>
              <div style={{ fontSize: 12, color: "#5C738A", fontFamily: "ui-monospace, monospace" }} data-testid="signature-domains">
                {domains.join(" · ")}
              </div>
            </section>

            <section style={{ background: "#fff", border: "1px solid #e2e2e2", borderRadius: 8, padding: 16 }}>
              <div style={{ fontSize: 10, letterSpacing: "0.3em", fontWeight: 700, color: "#5C738A", textTransform: "uppercase", marginBottom: 8 }}>HTML Source</div>
              <textarea
                data-testid="signature-html"
                readOnly
                value={html}
                style={{ width: "100%", minHeight: 220, fontFamily: "ui-monospace, monospace", fontSize: 11, padding: 12, border: "1px solid #e2e2e2", borderRadius: 4, whiteSpace: "pre", boxSizing: "border-box" }}
              />
            </section>

            <p style={{ marginTop: 16, fontSize: 12, color: "#5C738A" }}>
              <b>Install:</b> Gmail → Settings → See all settings → Signature → paste with <b>Copy Signature</b>. Apple Mail → Preferences → Signatures → drag or paste from this page (use Safari for best rich-copy support).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
